import * as React from "react";
import {Button, Divider, Grid, TextField, Typography} from "@material-ui/core";
import {API} from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import {Alert} from "@material-ui/lab";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import { AMAZON_ID } from "../../../../consts/Patterns";
import { HST_MIDNIGHT } from "../../../../consts/PacMan";

interface IProps {
    data?: any
}

interface IState {
    errorMessage: string
    isLoading: boolean
    personId: string
    drugId: string
    response: any
    finalizedDateStr: string
    dateOfServiceDateStr: string
    daysSupply: string
    calculatedNBD: string
}

const TABLE_NAMES = ['claim_groups', 'claims'];

type PromiseResult =
    | { status: 'fulfilled'; tableName: string; value: any }
    | { status: 'rejected'; tableName: string; reason: any };

enum InputField {
    PERSON_ID,
    FINALIZED_DATE,
    DATE_OF_SERVICE_DATE,
    DRUG_ID,
    DAYS_SUPPLY,
    CALCULATED_NBD
}

class UpdateNextBillableDateSection extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            errorMessage: '',
            isLoading: false,
            personId: '',
            drugId: '',
            response: '',
            finalizedDateStr: '',
            dateOfServiceDateStr: '',
            daysSupply: '30',
            calculatedNBD: ''
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleValidation = this.handleValidation.bind(this);
    }

    public render() {
        return(
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
                spacing={3}
            >
                <Grid item xs>
                    <Alert variant="filled" severity="info">
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li>Updates the Next Billing Date (NBD) for a given person and drug.</li>
                            <li>Two requests are sent: one for the claim_groups table and one for the claims table.</li>
                            <li>If Date of Service is not provided, it defaults to the Finalized Date.</li>
                            <li>NBD is calculated as Finalized Date + 80% of Days Supply of the drug (for reference only).</li>
                            <li>Selected dates are sent as midnight HST (Hawaii Standard Time) in yyyy-MM-dd'T'HH:mm:ssXXX format.</li>
                        </ul>
                    </Alert>
                </Grid>

                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        label="Person Id (Required)"
                        variant="outlined"
                        value={ this.state.personId }
                        onChange={ (event: React.ChangeEvent<HTMLInputElement>) => this.setRequestFields(InputField.PERSON_ID, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        label="Drug Id (Required)"
                        variant="outlined"
                        value={ this.state.drugId }
                        onChange={ (event: React.ChangeEvent<HTMLInputElement>) => this.setRequestFields(InputField.DRUG_ID, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        type="date"
                        label="Finalized Date (Required)"
                        variant="outlined"
                        value={ this.state.finalizedDateStr }
                        InputLabelProps={{ shrink: true }}
                        onChange={ (event: React.ChangeEvent<HTMLInputElement>) => this.setRequestFields(InputField.FINALIZED_DATE, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        type="date"
                        label="Date of Service Date (Optional)"
                        variant="outlined"
                        value={ this.state.dateOfServiceDateStr }
                        InputLabelProps={{ shrink: true }}
                        onChange={ (event: React.ChangeEvent<HTMLInputElement>) => this.setRequestFields(InputField.DATE_OF_SERVICE_DATE, event) }
                    />
                </Grid>
                <Grid item xs style={{width: 400}}>
                    <Divider />
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                        Preview - Finalized Date and Next Billable Date update each other based on Days Supply
                    </Typography>
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        type="number"
                        label="Days Supply (Info Only)"
                        variant="outlined"
                        value={ this.state.daysSupply }
                        onChange={ (event: React.ChangeEvent<HTMLInputElement>) => this.setRequestFields(InputField.DAYS_SUPPLY, event) }
                    />
                </Grid>
                <Grid item xs>
                    <TextField
                        style={{width: 400}}
                        type="date"
                        label="Next Billable Date"
                        variant="outlined"
                        value={ this.state.calculatedNBD }
                        InputLabelProps={{ shrink: true }}
                        onChange={ (event: React.ChangeEvent<HTMLInputElement>) => this.setRequestFields(InputField.CALCULATED_NBD, event) }
                    />
                </Grid>

                <Grid item xs>
                    <Button variant="outlined" color="default" onClick={ this.handleSubmit }>
                        Submit
                    </Button>
                </Grid>

                { this.state.isLoading && <LoadingSpinner/> }

                { this.state.errorMessage &&
                    <Alert variant="filled" severity="info">
                        Error encountered: { this.state.errorMessage }
                    </Alert>
                }

                { this.state.response &&
                    <div>
                        <br/>
                        <h4>Status</h4>
                        <p style={{
                            border: '3px solid lightgray',
                            padding: '5px',
                            whiteSpace: "pre-wrap",
                            wordBreak: "normal",
                            wordWrap: "break-word"
                        }}>
                            <JsonRenderComponent jsonObject={ this.state.response } printHtml={ false }/>
                        </p>
                    </div>
                }
            </Grid>
        );
    }

    private handleValidation() {
        if (!this.state.personId) {
            alert('Please enter the person id');
            return false;
        }
        if (!this.state.personId.match(AMAZON_ID)) {
            alert('Please enter the valid person id.');
            return false;
        }
        if (!this.state.finalizedDateStr) {
            alert('Please enter the finalized date');
            return false;
        }
        if (!this.state.drugId) {
            alert('Please enter the drug id');
            return false;
        }
        return true;
    }

    private setRequestFields(field: InputField, event: React.ChangeEvent<HTMLInputElement>) {
        switch (field) {
            case InputField.PERSON_ID: {
                this.setState({ personId: event.target.value });
                break;
            }
            case InputField.FINALIZED_DATE: {
                const finalizedDateStr = event.target.value;
                this.setState({ finalizedDateStr, calculatedNBD: this.calculateNBD(finalizedDateStr, this.state.daysSupply) });
                break;
            }
            case InputField.DATE_OF_SERVICE_DATE: {
                this.setState({ dateOfServiceDateStr: event.target.value });
                break;
            }
            case InputField.DRUG_ID: {
                this.setState({ drugId: event.target.value });
                break;
            }
            case InputField.DAYS_SUPPLY: {
                const daysSupply = event.target.value;
                this.setState({ daysSupply, calculatedNBD: this.calculateNBD(this.state.finalizedDateStr, daysSupply) });
                break;
            }
            case InputField.CALCULATED_NBD: {
                const calculatedNBD = event.target.value;
                this.setState({ calculatedNBD, finalizedDateStr: this.calculateFinalizedDate(calculatedNBD, this.state.daysSupply) });
                break;
            }
            default: {
                break;
            }
        }
    }



    private handleSubmit() {
        this.setState({ isLoading: true, errorMessage: '', response: '' });

        if (this.handleValidation()) {
            const baseBody = {
                personId: this.state.personId,
                finalizedDate: `${this.state.finalizedDateStr}${HST_MIDNIGHT}`,
                dateOfServiceDate: `${this.state.dateOfServiceDateStr || this.state.finalizedDateStr}${HST_MIDNIGHT}`,
                drugId: this.state.drugId
            };

            const apiName = 'ApaqPortalAPI';
            const path = '/pharmacy-customer/update-nbd';

            const promises = TABLE_NAMES.map(tableName =>
                API.post(apiName, path, {
                    body: { ...baseBody, tableName },
                    headers: {}
                }).then((res: any): PromiseResult => ({ status: 'fulfilled', tableName, value: res }))
                  .catch((err: any): PromiseResult => ({ status: 'rejected', tableName, reason: err }))
            );

            Promise.all(promises).then(results => {
                const successes = results
                    .filter((r): r is Extract<PromiseResult, { status: 'fulfilled' }> => r.status === 'fulfilled');
                const errors = results
                    .filter((r): r is Extract<PromiseResult, { status: 'rejected' }> => r.status === 'rejected');

                this.setState({
                    response: successes.length ? successes.map(({ tableName, value }) => ({ tableName, value })) : '',
                    errorMessage: this.buildErrorMessage(errors),
                    isLoading: false
                });
            });
        } else {
            this.setState({ isLoading: false });
        }
    }

    private buildErrorMessage(errors: Extract<PromiseResult, { status: 'rejected' }>[]): string {
        if (!errors.length) return '';
        return errors.map(e => {
            const message = e.reason?.response?.data?.message || e.reason?.message || 'Unknown error';
            return `${e.tableName}: ${message}`;
        }).join('; ');
    }

    private calculateDays(daysSupply: string): number {
        return Math.floor(parseFloat(daysSupply) * 0.8);
    }

    private calculateNBD(finalizedDateStr: string, daysSupply: string): string {
        if (!finalizedDateStr || !daysSupply) return '';
        const date = new Date(finalizedDateStr + 'T00:00:00');
        date.setDate(date.getDate() + this.calculateDays(daysSupply));
        return date.toISOString().split('T')[0];
    }

    private calculateFinalizedDate(nbdStr: string, daysSupply: string): string {
        if (!nbdStr || !daysSupply) return '';
        const date = new Date(nbdStr + 'T00:00:00');
        date.setDate(date.getDate() - this.calculateDays(daysSupply));
        return date.toISOString().split('T')[0];
    }


}

export default UpdateNextBillableDateSection;
